import React from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { CSSTransitionProps } from 'react-transition-group/CSSTransition'

import { TokenInterface } from '@store/actionSlices/token'
import { Template } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import useTemplateHandler from '@components/template-handler/use-template-handler'

import useCurrentPage from '@utilities/location-util'

interface WithPageTransitionProps {
  children: React.ReactNode
  projectIdentity: ProjectIdentity
  showcaseRoom: string
  token: TokenInterface
}

const fadingPages = ['vision', 'dashboard', 'login', '']
const slidingRightPages = ['level-view', 'area-view']
const slidingLeftPages = ['level-view', 'building']
const slidingPagesV2 = ['location', 'interactive-map']

function WithPageTransition({
  children,
  projectIdentity,
  showcaseRoom,
  token,
}: WithPageTransitionProps) {
  const location = useLocation()
  const [currentPage, previousPage] = useCurrentPage()
  const selectedTemplate = useTemplateHandler({
    projectIdentity,
    showcaseRoom,
    token,
  })

  const transitionProps = React.useMemo(() => {
    const props: CSSTransitionProps = {
      classNames: '',
      timeout: selectedTemplate === Template.V2 ? 300 : 500,
    }

    if (selectedTemplate === Template.V2) {
      if (slidingPagesV2.includes(currentPage)) {
        props.classNames = 'transition-slide'
        return props
      }
      if (slidingPagesV2.includes(previousPage)) {
        props.classNames = 'transition-fade-ease-slide'
        return props
      }
      props.classNames = 'transition-fade-ease'
      return props
    }

    if (
      fadingPages.includes(currentPage) ||
      fadingPages.includes(previousPage)
    ) {
      props.classNames = 'transition-fade-ease'
      return props
    }

    if (
      slidingRightPages.includes(currentPage) &&
      slidingLeftPages.includes(previousPage)
    ) {
      props.classNames = 'transition-slide-right'
      return props
    }

    if (
      slidingLeftPages.includes(currentPage) &&
      slidingRightPages.includes(previousPage)
    ) {
      props.classNames = 'transition-slide-left'
      return props
    }

    props.classNames = 'transition-slide-top'
    return props
  }, [location, selectedTemplate])

  return (
    <TransitionGroup
      childFactory={(child) =>
        React.cloneElement(child, { ...transitionProps })
      }
      className="relative h-screen w-screen overflow-hidden"
    >
      <CSSTransition
        key={location.pathname}
        timeout={300}
        mountOnEnter
        unmountOnExit
      >
        {children}
      </CSSTransition>
    </TransitionGroup>
  )
}

export default connect(
  ({
    projectIdentity,
    token,
    appConfig: { room: showcaseRoom },
  }: RootStateTypeExtra) => ({
    showcaseRoom,
    projectIdentity,
    token,
  })
)(WithPageTransition)
